// src/Commoncontext/AnnouncementsContext.js
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import AnnouncementsApi from "../Api/AnnouncementsApi";
import { useSelectedCompany } from "./SelectedCompanyContext";

const AnnouncementsContext = createContext();

/* ===========================
   Local ETag cache
=========================== */
const CACHE_KEY = "ann_cache_etag_v3";

function createCache() {
  return {
    map: new Map(), // companyId -> { data, etag, version, fetchedAt }
    hydrate() {
      try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (raw) this.map = new Map(Object.entries(JSON.parse(raw)));
      } catch {}
    },
    persist() {
      try {
        localStorage.setItem(
          CACHE_KEY,
          JSON.stringify(Object.fromEntries(this.map))
        );
      } catch {}
    },
  };
}

export const AnnouncementsProvider = ({ children }) => {
  const { selectedCompanyId } = useSelectedCompany();
  const cache = useRef(createCache()).current;

  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);

  const [active, setActive] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Detail cache (nopea selaus edestakaisin ilman uutta fetchiä)
  const detailCacheRef = useRef(new Map()); // key -> { data, fetchedAt }
  const inFlightRef = useRef(new Map()); // key -> Promise

  useEffect(() => {
    cache.hydrate();
  }, [cache]);

  const invalidateListCache = useCallback(
    (companyId) => {
      if (!companyId) return;
      const entry = cache.map.get(companyId);
      if (entry) {
        cache.map.set(companyId, { ...entry, etag: undefined });
        cache.persist();
      }
    },
    [cache]
  );

  const getDetailKey = useCallback((companyId, announcementId) => {
    return `${companyId || ""}:${announcementId || ""}`;
  }, []);

  /* ===========================
     LIST
  =========================== */
  const fetchAnnouncements = useCallback(
    async (companyId) => {
      if (!companyId) return;

      const entry = cache.map.get(companyId);
      if (entry?.data) {
        setAnnouncements(entry.data);
      }

      setLoading(!entry);

      try {
        const resp = await AnnouncementsApi.fetchAnnouncements(companyId, {
          etag: entry?.etag,
        });

        if (resp?.status === 304) return;

        const data = Array.isArray(resp?.announcements)
          ? resp.announcements
          : [];

        cache.map.set(companyId, {
          data,
          etag: resp?.etag,
          version: resp?.version,
          fetchedAt: Date.now(),
        });
        cache.persist();

        setAnnouncements(data);
      } catch (e) {
        console.error("fetchAnnouncements error:", e);
        if (!entry) setAnnouncements([]);
      } finally {
        setLoading(false);
      }
    },
    [cache]
  );

  /* ===========================
     DETAIL
     - dedupe in-flight
     - local cache for fast back/forward
  =========================== */
  const fetchAnnouncementById = useCallback(
    async (announcementId, opts = {}) => {
      const companyId = selectedCompanyId;
      if (!companyId || !announcementId) return null;

      const { preferCache = true, force = false } = opts || {};
      const key = getDetailKey(companyId, announcementId);

      if (!force && preferCache) {
        const cached = detailCacheRef.current.get(key);
        if (cached?.data) {
          // Näytä heti cache
          setActive(cached.data);
          // älä blokkaa UI:ta detailLoadingilla
          return cached.data;
        }
      }

      // dedupe: jos sama request in-flight, käytä samaa promisea
      const inflight = inFlightRef.current.get(key);
      if (inflight) {
        setDetailLoading(true);
        try {
          const data = await inflight;
          return data;
        } finally {
          setDetailLoading(false);
        }
      }

      setDetailLoading(true);

      const p = (async () => {
        try {
          const data = await AnnouncementsApi.fetchAnnouncementById(
            companyId,
            announcementId
          );
          setActive(data || null);
          detailCacheRef.current.set(key, {
            data: data || null,
            fetchedAt: Date.now(),
          });
          return data || null;
        } catch (e) {
          console.error("fetchAnnouncementById error:", e);
          setActive(null);
          return null;
        } finally {
          inFlightRef.current.delete(key);
        }
      })();

      inFlightRef.current.set(key, p);

      try {
        return await p;
      } finally {
        setDetailLoading(false);
      }
    },
    [selectedCompanyId, getDetailKey]
  );

  /* ===========================
     CREATE / UPDATE
  =========================== */
  const createAnnouncement = useCallback(
    async (companyId, payload) => {
      const res = await AnnouncementsApi.createAnnouncement({
        companyId,
        ...payload,
      });

      const now = Date.now();

      const optimistic = {
        id: res.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
        lastCommentAt: null,
        attachments: Array.isArray(payload?.attachments)
          ? payload.attachments
          : [],
        comments: [],
      };

      setAnnouncements((prev) => [optimistic, ...(prev || [])]);

      invalidateListCache(companyId);

      // detail cache prefill
      const key = getDetailKey(companyId, res.id);
      detailCacheRef.current.set(key, { data: optimistic, fetchedAt: now });
      setActive(optimistic);

      return res;
    },
    [invalidateListCache, getDetailKey]
  );

  const updateAnnouncement = useCallback(
    async (companyId, announcementId, patch) => {
      await AnnouncementsApi.updateAnnouncement(companyId, announcementId, patch);

      const now = Date.now();

      setAnnouncements((prev) =>
        (prev || []).map((a) =>
          String(a.id) === String(announcementId)
            ? { ...a, ...patch, updatedAt: now }
            : a
        )
      );

      setActive((prev) =>
        prev && String(prev.id) === String(announcementId)
          ? { ...prev, ...patch, updatedAt: now }
          : prev
      );

      // update detail cache if exists
      const key = getDetailKey(companyId, announcementId);
      const cached = detailCacheRef.current.get(key);
      if (cached?.data) {
        detailCacheRef.current.set(key, {
          data: { ...cached.data, ...patch, updatedAt: now },
          fetchedAt: Date.now(),
        });
      }

      invalidateListCache(companyId);
    },
    [invalidateListCache, getDetailKey]
  );

  /* ===========================
     COMMENTS (optimistinen + ei refetch-looppeja)
  =========================== */
  const addCommentOptimistic = useCallback(
    (companyId, announcementId, optimisticComment) => {
      if (!companyId || !announcementId || !optimisticComment) return;

      setActive((prev) => {
        if (!prev || String(prev.id) !== String(announcementId)) return prev;
        const prevComments = Array.isArray(prev.comments)
          ? prev.comments
          : Array.isArray(prev.messages)
          ? prev.messages
          : [];
        // de-dupe by id
        if (
          optimisticComment?.id &&
          prevComments.some((c) => String(c?.id) === String(optimisticComment.id))
        ) {
          return prev;
        }
        return {
          ...prev,
          comments: [...prevComments, optimisticComment],
          lastCommentAt: Date.now(),
          updatedAt: Date.now(),
        };
      });

      const key = getDetailKey(companyId, announcementId);
      const cached = detailCacheRef.current.get(key)?.data;
      if (cached) {
        const prevComments = Array.isArray(cached.comments)
          ? cached.comments
          : Array.isArray(cached.messages)
          ? cached.messages
          : [];
        if (
          optimisticComment?.id &&
          prevComments.some((c) => String(c?.id) === String(optimisticComment.id))
        ) {
          return;
        }
        detailCacheRef.current.set(key, {
          data: {
            ...cached,
            comments: [...prevComments, optimisticComment],
            lastCommentAt: Date.now(),
            updatedAt: Date.now(),
          },
          fetchedAt: Date.now(),
        });
      }
    },
    [getDetailKey]
  );

  const replaceOptimisticComment = useCallback(
    (companyId, announcementId, tempId, saved) => {
      if (!companyId || !announcementId || !tempId) return;

      const apply = (entity) => {
        const arr = Array.isArray(entity?.comments)
          ? entity.comments
          : Array.isArray(entity?.messages)
          ? entity.messages
          : [];
        const next = arr
          .filter((c) => String(c?.id) !== String(tempId))
          .concat(saved ? [saved] : []);
        // de-dupe by saved id
        const seen = new Set();
        const deduped = [];
        for (const c of next) {
          const id = c?.id != null ? String(c.id) : null;
          if (id && seen.has(id)) continue;
          if (id) seen.add(id);
          deduped.push(c);
        }
        deduped.sort((a, b) => (a?.createdAt || 0) - (b?.createdAt || 0));
        return { ...entity, comments: deduped, lastCommentAt: Date.now(), updatedAt: Date.now() };
      };

      setActive((prev) => {
        if (!prev || String(prev.id) !== String(announcementId)) return prev;
        return apply(prev);
      });

      const key = getDetailKey(companyId, announcementId);
      const cached = detailCacheRef.current.get(key)?.data;
      if (cached) {
        detailCacheRef.current.set(key, { data: apply(cached), fetchedAt: Date.now() });
      }
    },
    [getDetailKey]
  );

  const addComment = useCallback(
    async (companyId, announcementId, payload) => {
      // HUOM: ei refetchiä täällä (se aiheuttaa helposti vilkkumisen)
      const saved = await AnnouncementsApi.addAnnouncementComment(
        companyId,
        announcementId,
        payload
      );
      return saved;
    },
    []
  );

  /* ===========================
     ATTACHMENTS (optimistinen, ei refetch)
  =========================== */
  const uploadAttachment = useCallback(
    async (companyId, announcementId, file) => {
      const att = await AnnouncementsApi.uploadAnnouncementAttachment(
        companyId,
        announcementId,
        file
      );

      setActive((prev) => {
        if (!prev || String(prev.id) !== String(announcementId)) return prev;
        const prevAtt = Array.isArray(prev.attachments) ? prev.attachments : [];
        if (att?.id && prevAtt.some((a) => String(a?.id) === String(att.id)))
          return prev;
        return {
          ...prev,
          attachments: [...prevAtt, att],
          updatedAt: Date.now(),
        };
      });

      const key = getDetailKey(companyId, announcementId);
      const cached = detailCacheRef.current.get(key)?.data;
      if (cached) {
        const prevAtt = Array.isArray(cached.attachments)
          ? cached.attachments
          : [];
        if (att?.id && prevAtt.some((a) => String(a?.id) === String(att.id)))
          return att;
        detailCacheRef.current.set(key, {
          data: { ...cached, attachments: [...prevAtt, att], updatedAt: Date.now() },
          fetchedAt: Date.now(),
        });
      }

      invalidateListCache(companyId);
      return att;
    },
    [invalidateListCache, getDetailKey]
  );

  const uploadAttachments = useCallback(
    async (companyId, announcementId, files) => {
      const out = [];
      for (const f of Array.from(files || [])) {
        out.push(await uploadAttachment(companyId, announcementId, f));
      }
      return out;
    },
    [uploadAttachment]
  );

  const deleteAttachment = useCallback(
    async (companyId, announcementId, attachmentId) => {
      await AnnouncementsApi.deleteAnnouncementAttachment(
        companyId,
        announcementId,
        attachmentId
      );

      setActive((prev) => {
        if (!prev || String(prev.id) !== String(announcementId)) return prev;
        return {
          ...prev,
          attachments: (prev.attachments || []).filter(
            (a) => String(a?.id) !== String(attachmentId)
          ),
          updatedAt: Date.now(),
        };
      });

      const key = getDetailKey(companyId, announcementId);
      const cached = detailCacheRef.current.get(key)?.data;
      if (cached) {
        detailCacheRef.current.set(key, {
          data: {
            ...cached,
            attachments: (cached.attachments || []).filter(
              (a) => String(a?.id) !== String(attachmentId)
            ),
            updatedAt: Date.now(),
          },
          fetchedAt: Date.now(),
        });
      }

      invalidateListCache(companyId);
    },
    [invalidateListCache, getDetailKey]
  );

  /* ===========================
     VIEWED (idempotent, EI turhaa setActive)
  =========================== */
  const markViewed = useCallback(async (announcementId, userId, companyId) => {
    if (!companyId || !announcementId || !userId) return;

    await AnnouncementsApi.markAnnouncementAsViewed(
      companyId,
      announcementId,
      userId
    );

    // Päivitä active vain jos se oikeasti muuttuu
    setActive((prev) => {
      if (!prev || String(prev.id) !== String(announcementId)) return prev;
      const viewedBy = Array.isArray(prev.viewedBy) ? prev.viewedBy : [];
      const uidStr = String(userId);
      if (viewedBy.includes(uidStr)) return prev; // ✅ estää turhat renderit
      return { ...prev, viewedBy: [...viewedBy, uidStr] };
    });
  }, []);

  /* ===========================
     DELETE
  =========================== */
  const deleteAnnouncement = useCallback(
    async (companyId, announcementId) => {
      await AnnouncementsApi.deleteAnnouncement(companyId, announcementId);

      setAnnouncements((prev) =>
        (prev || []).filter((a) => String(a.id) !== String(announcementId))
      );

      // clear caches
      const key = getDetailKey(companyId, announcementId);
      detailCacheRef.current.delete(key);

      invalidateListCache(companyId);

      setActive((prev) =>
        prev && String(prev.id) === String(announcementId) ? null : prev
      );
    },
    [invalidateListCache, getDetailKey]
  );

  /* ===========================
   LIKES (optimistinen + cacheen -> ei häviä kun palaa listasta)
=========================== */
const likeComment = useCallback(
  async (companyId, announcementId, commentId, userId, userName) => {
    if (!companyId || !announcementId || !commentId || !userId || !userName) return null;

    const key = getDetailKey(companyId, announcementId);

    // helper: päivitä yhden kommentin likedBy/likes
    const applyToggle = (entity, mode = "toggle", forcedLikedBy = null) => {
      if (!entity) return { next: entity, snapshot: null };

      const pickArr = () =>
        Array.isArray(entity.comments)
          ? entity.comments
          : Array.isArray(entity.messages)
          ? entity.messages
          : [];

      const arr = pickArr();
      let snapshot = null;

      const nextArr = arr.map((c) => {
        if (String(c?.id) !== String(commentId)) return c;

        const likedBy = Array.isArray(c?.likedBy) ? c.likedBy : [];
        snapshot = { likedBy: [...likedBy], likes: typeof c?.likes === "number" ? c.likes : likedBy.length };

        let nextLikedBy = likedBy;

        if (Array.isArray(forcedLikedBy)) {
          nextLikedBy = forcedLikedBy;
        } else if (mode === "toggle") {
          const already = likedBy.includes(userName);
          nextLikedBy = already ? likedBy.filter((n) => n !== userName) : [...likedBy, userName];
        }

        return {
          ...c,
          likedBy: nextLikedBy,
          likes: nextLikedBy.length,
        };
      });

      return {
        next: {
          ...entity,
          comments: Array.isArray(entity.comments) ? nextArr : entity.comments,
          messages: Array.isArray(entity.messages) ? nextArr : entity.messages,
          updatedAt: Date.now(),
        },
        snapshot,
      };
    };

    // 1) Optimistinen päivitys activeen + detailCacheen (tämä on se oleellinen fix)
    let rollbackSnapshot = null;

    setActive((prev) => {
      if (!prev || String(prev.id) !== String(announcementId)) return prev;
      const { next, snapshot } = applyToggle(prev, "toggle");
      rollbackSnapshot = snapshot;
      return next;
    });

    const cached = detailCacheRef.current.get(key)?.data;
    if (cached) {
      const { next } = applyToggle(cached, "toggle");
      detailCacheRef.current.set(key, { data: next, fetchedAt: Date.now() });
    }

    try {
      // 2) Persistoi backendille
      const out = await AnnouncementsApi.likeAnnouncementComment({
        companyId,
        announcementId,
        commentId,
        userId,
        userName,
      });

      // 3) Jos backend palauttaa "totuuden" (likedBy/likes), synkkaa se
      // (jos ei palauta mitään, optimistinen jää voimaan)
      if (out && (Array.isArray(out.likedBy) || typeof out.likes === "number")) {
        const forcedLikedBy = Array.isArray(out.likedBy) ? out.likedBy : null;

        setActive((prev) => {
          if (!prev || String(prev.id) !== String(announcementId)) return prev;
          const { next } = applyToggle(prev, "apply", forcedLikedBy);
          return next;
        });

        const cached2 = detailCacheRef.current.get(key)?.data;
        if (cached2) {
          const { next } = applyToggle(cached2, "apply", forcedLikedBy);
          detailCacheRef.current.set(key, { data: next, fetchedAt: Date.now() });
        }
      }

      return out || { ok: true };
    } catch (e) {
      // 4) Rollback jos persistointi epäonnistuu
      if (rollbackSnapshot) {
        setActive((prev) => {
          if (!prev || String(prev.id) !== String(announcementId)) return prev;

          const pickArr = () =>
            Array.isArray(prev.comments)
              ? prev.comments
              : Array.isArray(prev.messages)
              ? prev.messages
              : [];

          const arr = pickArr();

          const nextArr = arr.map((c) => {
            if (String(c?.id) !== String(commentId)) return c;
            return { ...c, likedBy: rollbackSnapshot.likedBy, likes: rollbackSnapshot.likes };
          });

          return {
            ...prev,
            comments: Array.isArray(prev.comments) ? nextArr : prev.comments,
            messages: Array.isArray(prev.messages) ? nextArr : prev.messages,
          };
        });

        const cached3 = detailCacheRef.current.get(key)?.data;
        if (cached3) {
          const arr = Array.isArray(cached3.comments)
            ? cached3.comments
            : Array.isArray(cached3.messages)
            ? cached3.messages
            : [];

          const nextArr = arr.map((c) => {
            if (String(c?.id) !== String(commentId)) return c;
            return { ...c, likedBy: rollbackSnapshot.likedBy, likes: rollbackSnapshot.likes };
          });

          detailCacheRef.current.set(key, {
            data: {
              ...cached3,
              comments: Array.isArray(cached3.comments) ? nextArr : cached3.comments,
              messages: Array.isArray(cached3.messages) ? nextArr : cached3.messages,
            },
            fetchedAt: Date.now(),
          });
        }
      }

      throw e;
    }
  },
  [getDetailKey]
);


  const value = useMemo(
    () => ({
      announcements,
      loading,
      fetchAnnouncements,

      active,
      detailLoading,
      fetchAnnouncementById,
      setActive,

      createAnnouncement,
      updateAnnouncement,

      // comments
      addComment,
      addCommentOptimistic,
      replaceOptimisticComment,
      likeComment,

      // attachments
      uploadAttachment,
      uploadAttachments,
      deleteAttachment,

      // other
      markViewed,
      deleteAnnouncement,
    }),
    [
      announcements,
      loading,
      fetchAnnouncements,
      active,
      detailLoading,
      fetchAnnouncementById,
      createAnnouncement,
      updateAnnouncement,
      addComment,
      addCommentOptimistic,
      replaceOptimisticComment,
      likeComment,
      uploadAttachment,
      uploadAttachments,
      deleteAttachment,
      markViewed,
      deleteAnnouncement,
    ]
  );

  return (
    <AnnouncementsContext.Provider value={value}>
      {children}
    </AnnouncementsContext.Provider>
  );
};

export const useAnnouncements = () => useContext(AnnouncementsContext);
